<template>
  <div class="ov-page db">
    <!-- ===== EN-TÊTE : ACCUEIL + FILTRES ===== -->
    <div class="db-head">
      <div class="db-head__intro">
        <div class="db-head__date">{{ currentDate }}</div>
        <h1 class="db-head__title">{{ $t('dashboard.welcome') }}, {{ $store.state.userAuthentified.prenoms || $store.state.userAuthentified.nom }}</h1>
        <div v-if="$store.state.userAuthentified.lastConnection" class="db-head__sub">
          {{ $t('dashboard.lastConnection') }} {{ $store.state.userAuthentified.lastConnection }}
        </div>
      </div>

      <div class="db-filters">
        <select v-model="dateSelected" class="ov-select" :aria-label="$t('dashboard.byDate')">
          <option :value="null" disabled>{{ $t('dashboard.byDate') }}</option>
          <option v-for="(item, index) in dateFilterList" :key="index" :value="item.title">{{ item.title }}</option>
        </select>
        <select v-model="sectionGroup" class="ov-select" :aria-label="$t('dashboard.section')">
          <option :value="null" disabled>{{ $t('dashboard.section') }}</option>
          <option v-for="(item, index) in sections" :key="index" :value="item.title">{{ item.title }}</option>
        </select>
        <template v-if="dateSelected === 'Periodique'">
          <input v-model="dateDebut" type="date" class="ov-input" :aria-label="$t('dashboard.startDate')" :title="$t('dashboard.startDate')" />
          <input v-model="dateFin" type="date" class="ov-input" :aria-label="$t('dashboard.endDate')" :title="$t('dashboard.endDate')" />
        </template>
        <input
          v-if="dateSelected === 'À une date précise'"
          v-model="datePrecise"
          type="date"
          class="ov-input"
          :aria-label="$t('dashboard.preciseDate')"
          :title="$t('dashboard.preciseDate')"
        />
        <select
          v-if="sectionGroup === 'Gare de transport'"
          v-model="gareDesignationSelected"
          class="ov-select"
          :aria-label="$t('dashboard.transportStation')"
        >
          <option :value="null" disabled>{{ $t('dashboard.transportStation') }}</option>
          <option v-for="gare in gareDisponiblesList" :key="gare.designation" :value="gare.designation">{{ gare.designation }}</option>
        </select>
      </div>
    </div>

    <!-- ===== STATISTIQUES D'UNE GARE (FILTRE) ===== -->
    <section v-if="isFilterApply == true && sectionGroup == 'Gare de transport'" class="db-section">
      <div class="db-section__head">
        <h2 class="db-section__title">{{ $t('dashboard.stationStatistics') }} {{ gareDesignationSelected }}</h2>
        <span class="ov-badge ov-badge--navy ov-badge--plain">{{ getTextPeriod }}</span>
      </div>
      <StatistiqueReservationParGareTransport
        :data-statistique-gare-transport="dataStatisticGareTransport"
        :chartDataGare="chartDataGare"
        :chart-data-doughnut-gare="chartDataDoughnutGare"
      ></StatistiqueReservationParGareTransport>
    </section>

    <!-- ===== AUJOURD'HUI ===== -->
    <section class="db-section">
      <div class="db-section__head">
        <h2 class="db-section__title">{{ $t('dashboard.dailyStatistics') }}</h2>
        <span class="ov-badge ov-badge--success">Aujourd'hui</span>
      </div>

      <div class="ov-kpis ov-kpis--4">
        <div class="ov-kpi ov-kpi--primary">
          <div class="ov-kpi__label">{{ $t('dashboard.revenue') }}</div>
          <div class="ov-kpi__value">{{ formatNumber(dataStatisticsToday && dataStatisticsToday.chiffreAffaires) }}<span>FCFA</span></div>
        </div>
        <div class="ov-kpi">
          <div class="ov-kpi__label">{{ $t('dashboard.totalReservations') }}</div>
          <div class="ov-kpi__value">{{ formatNumber(dataStatisticsToday && dataStatisticsToday.nombreTotalReservationBilletVoyage) }}</div>
        </div>
        <div class="ov-kpi">
          <div class="ov-kpi__label">{{ $t('dashboard.luggageRevenue') }}</div>
          <div class="ov-kpi__value">{{ formatNumber(dataStatisticsToday && dataStatisticsToday.chiffreAffairesBagages) }}<span>FCFA</span></div>
        </div>
        <div class="ov-kpi">
          <div class="ov-kpi__label">Offres actives</div>
          <div class="ov-kpi__value">{{ activeOffersCount }}<span>/ {{ offreVoyageDisponibleList.length }}</span></div>
        </div>
      </div>

      <div class="ov-charts">
        <div class="ov-card">
          <div class="ov-card__head">
            <div>
              <div class="ov-card__title">Chiffre d'affaires par offre</div>
              <div class="ov-card__sub">Réservations et bagages, en FCFA</div>
            </div>
          </div>
          <div class="ov-card__body">
            <Bar v-if="hasData(todayRevenueChart)" chart-id="db-today-revenue" :chart-data="todayRevenueChart" :chart-options="revenueChartOptions" :styles="chartStyles" />
            <div v-else class="ov-empty ov-chart-empty">Aucune réservation enregistrée aujourd'hui.</div>
          </div>
        </div>
        <div class="ov-card">
          <div class="ov-card__head">
            <div>
              <div class="ov-card__title">Réservations par offre</div>
              <div class="ov-card__sub">Classement du jour</div>
            </div>
          </div>
          <div class="ov-card__body">
            <Bar v-if="hasData(todayRankingChart)" chart-id="db-today-ranking" :chart-data="todayRankingChart" :chart-options="rankingChartOptions" :styles="rankingHeight(todayRankingChart)" />
            <div v-else class="ov-empty ov-chart-empty">Aucune réservation aujourd'hui.</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== ANNÉE ===== -->
    <section class="db-section">
      <div class="db-section__head">
        <h2 class="db-section__title">{{ $t('dashboard.annualStatistics') }}</h2>
        <span class="ov-badge ov-badge--navy ov-badge--plain">{{ new Date().getFullYear() }}</span>
      </div>

      <div class="ov-kpis">
        <div class="ov-kpi ov-kpi--primary">
          <div class="ov-kpi__label">{{ $t('dashboard.revenue') }}</div>
          <div class="ov-kpi__value">{{ formatNumber(dataStatistics && dataStatistics.chiffreAffaires) }}<span>FCFA</span></div>
        </div>
        <div class="ov-kpi">
          <div class="ov-kpi__label">{{ $t('dashboard.totalReservations') }}</div>
          <div class="ov-kpi__value">{{ formatNumber(dataStatistics && dataStatistics.nombreTotalReservationBilletVoyage) }}</div>
        </div>
        <div class="ov-kpi">
          <div class="ov-kpi__label">{{ $t('dashboard.luggageRevenue') }}</div>
          <div class="ov-kpi__value">{{ formatNumber(dataStatistics && dataStatistics.chiffreAffairesBagages) }}<span>FCFA</span></div>
        </div>
      </div>

      <div class="ov-charts">
        <div class="ov-card">
          <div class="ov-card__head">
            <div>
              <div class="ov-card__title">Chiffre d'affaires par offre</div>
              <div class="ov-card__sub">Réservations et bagages, en FCFA</div>
            </div>
          </div>
          <div class="ov-card__body">
            <Bar v-if="hasData(yearRevenueChart)" chart-id="db-year-revenue" :chart-data="yearRevenueChart" :chart-options="revenueChartOptions" :styles="chartStyles" />
            <div v-else class="ov-empty ov-chart-empty">Aucune donnée pour l'année en cours.</div>
          </div>
        </div>
        <div class="ov-card">
          <div class="ov-card__head">
            <div>
              <div class="ov-card__title">Réservations par offre</div>
              <div class="ov-card__sub">Classement de l'année</div>
            </div>
          </div>
          <div class="ov-card__body">
            <Bar v-if="hasData(yearRankingChart)" chart-id="db-year-ranking" :chart-data="yearRankingChart" :chart-options="rankingChartOptions" :styles="rankingHeight(yearRankingChart)" />
            <div v-else class="ov-empty ov-chart-empty">Aucune réservation cette année.</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== OFFRES DE VOYAGE ===== -->
    <section class="db-section">
      <div class="db-section__head">
        <h2 class="db-section__title">{{ $t('dashboard.travelOffersStatistics') }}</h2>
        <span class="ov-badge ov-badge--navy ov-badge--plain">{{ getTextPeriod }}</span>
      </div>

      <div class="ov-card">
        <div class="ov-card__head">
          <div class="ov-card__title">
            Offres
            <span class="ov-count">{{ filteredOffreVoyageList.length }}</span>
          </div>
          <div class="ov-filters">
            <div class="db-seg" role="group" aria-label="Filtrer par statut">
              <button type="button" class="db-seg__opt" :class="{ 'db-seg__opt--active': filterActif === null }" @click="filterActif = null">Toutes</button>
              <button type="button" class="db-seg__opt" :class="{ 'db-seg__opt--active': filterActif === true }" @click="filterActif = true">Actives</button>
              <button type="button" class="db-seg__opt" :class="{ 'db-seg__opt--active': filterActif === false }" @click="filterActif = false">Inactives</button>
            </div>
            <div class="ov-search">
              <v-icon small>mdi-magnify</v-icon>
              <input v-model="search" type="text" class="ov-input" placeholder="Rechercher une offre..." />
            </div>
          </div>
        </div>

        <v-data-table
          class="ov-table"
          :headers="headers"
          :items="filteredOffreVoyageList"
          :loading="loading"
          :search="search"
          loader-height="2"
          :items-per-page="5"
          no-data-text="Aucune offre de voyage"
        >
          <template v-slot:[`item.designation`]="{ item }">
            <span class="db-offer-name" :class="{ 'db-offer-name--selected': selectedOfferDesignation === item.designation }">
              {{ item.designation }}
            </span>
          </template>
          <template v-slot:[`item.isActif`]="{ item }">
            <span v-if="item.isActif" class="ov-badge ov-badge--success">{{ $t('dashboard.activeStatus') }}</span>
            <span v-else class="ov-badge ov-badge--neutral">{{ $t('dashboard.inactiveStatus') }}</span>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <div class="ov-row-actions db-actions">
              <button
                type="button"
                class="ov-btn ov-btn--secondary ov-btn--sm"
                :title="$t('dashboard.moreDetails')"
                @click="getStatisticsOffreVoyageCompagnie(item.designation)"
              >
                <v-icon small>mdi-chart-bar</v-icon>
                Statistiques
              </button>
            </div>
          </template>
        </v-data-table>
      </div>

      <!-- Détail de l'offre sélectionnée -->
      <div v-if="offreVoyageStatistique != null" class="db-offer">
        <div class="db-offer__head">
          <div>
            <div class="ov-card__kicker">Offre sélectionnée</div>
            <div class="db-offer__title">{{ offreVoyageStatistique.offreVoyageDesignation }}</div>
          </div>
          <button type="button" class="ov-icon-btn" title="Fermer" @click="clearOfferSelection">
            <v-icon small>mdi-close</v-icon>
          </button>
        </div>

        <div class="ov-kpis">
          <div class="ov-kpi ov-kpi--primary">
            <div class="ov-kpi__label">{{ $t('dashboard.revenue') }}</div>
            <div class="ov-kpi__value">{{ formatNumber(offreVoyageStatistique.chiffreAffairesReservation) }}<span>FCFA</span></div>
          </div>
          <div class="ov-kpi">
            <div class="ov-kpi__label">{{ $t('dashboard.totalReservations') }}</div>
            <div class="ov-kpi__value">{{ formatNumber(offreVoyageStatistique.nombreReservationBilletVoyage) }}</div>
          </div>
          <div class="ov-kpi">
            <div class="ov-kpi__label">{{ $t('dashboard.luggageRevenue') }}</div>
            <div class="ov-kpi__value">{{ formatNumber(offreVoyageStatistique.chiffreAffairesBagage) }}<span>FCFA</span></div>
          </div>
        </div>

        <div class="ov-charts">
          <div class="ov-card">
            <div class="ov-card__head">
              <div>
                <div class="ov-card__title">Chiffre d'affaires par programme</div>
                <div class="ov-card__sub">Réservations et bagages, en FCFA</div>
              </div>
            </div>
            <div class="ov-card__body">
              <Bar v-if="hasData(programRevenueChart)" chart-id="db-program-revenue" :chart-data="programRevenueChart" :chart-options="revenueChartOptions" :styles="chartStyles" />
              <div v-else class="ov-empty ov-chart-empty">Aucune donnée pour cette offre sur la période.</div>
            </div>
          </div>
          <div class="ov-card">
            <div class="ov-card__head">
              <div>
                <div class="ov-card__title">Réservations par programme</div>
                <div class="ov-card__sub">Classement sur la période</div>
              </div>
            </div>
            <div class="ov-card__body">
              <Bar v-if="hasData(programRankingChart)" chart-id="db-program-ranking" :chart-data="programRankingChart" :chart-options="rankingChartOptions" :styles="rankingHeight(programRankingChart)" />
              <div v-else class="ov-empty ov-chart-empty">Aucune réservation pour cette offre.</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <v-alert class="myalert alert-success" type="success" width="350px" dismissible>{{ successMsg }}</v-alert>
    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
  </div>
</template>


<script>
import { statisticsService, offerService, stationService, documentService } from '@/services';
import { Bar } from "vue-chartjs/legacy";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  LinearScale,
  CategoryScale,
  ArcElement,
  PointElement,
  BarElement,
} from "chart.js";
import {
  SERIES_RESERVATION,
  SERIES_LUGGAGE,
  CHART_STYLES,
  chartOptions,
  formatNumber,
  buildGroupedBars,
  buildRanking,
  hasData,
  rankingHeight,
} from "@/modules/statistiques/chartTheme";
import StatistiqueReservationParGareTransport from "@/modules/statistiques/traders/views/StatistiqueReservationParGareTransport.vue";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  LinearScale,
  ArcElement,
  CategoryScale,
  PointElement,
  BarElement
);


export default {
  name: "DashboardAdminTransportCompanie",
  components: {
    StatistiqueReservationParGareTransport,
    Bar,
  },

  data() {
    return {
      errorMsg: null,
      successMsg: null,

      loading: true,
      search: "",
      headers: [
        { text: this.$t('dashboard.designation'), value: "designation" },
        { text: this.$t('dashboard.offerType'), value: "typeOffreVoyageDesignation" },
        { text: this.$t('dashboard.active'), value: "isActif" },
        { text: this.$t('dashboard.actions'), value: "actions", sortable: false },
      ],

      filterActif: null,

      offreVoyageDisponibleList: [],

      offreVoyageObject: {
        data: {
          compagnieTransportRaisonSociale: null,
        },
      },









      chartDataGare: {
        labels: [],
        datasets: [
          {
            label: "Chiffre Affaire Réservation (Annuel)",
            backgroundColor: "#2f3542",
            data: [],
          },
          {
            label: "Chiffre Affaire Bagage (Annuel)",
            backgroundColor: "#2ed573",
            data: [],
          },
        ],
      },

      chartDataDoughnutGare: {
        labels: [],
        datasets: [
          {
            backgroundColor: [
              "#2f3542",
              "#2ed573",
              "#ffd32a",
              "#ffa801",
              "#3c40c6",
              "#f53b57",
              "#182C61",
              "#9AECDB",
              "#BDC581",
              "#82589F",
              "#EAB543",
              "#079992",
            ],
            data: [],
          },
        ],
      },

      isFilterApply: false,

      dateFilterList: [
        { title: this.$t('dashboard.periodic'), value: "period" },
        { title: this.$t('dashboard.specificDate'), value: "date" },
      ],

      sections: [{ title: this.$t('dashboard.transportStation') }, { title: this.$t('dashboard.myCompany') }],

      sectionGroup: null,
      dateSelected: null,
      
      isInitialLoad: true,

      reservationList: [],

      photoProfilObject: {
        data: {
          typeDocument: null,
        },
      },

      dataToSend: {
        data: {
          raisonSociale: null,
          dateGiven: null,
          period: {
            dateDebut: null,
            dateFin: null,
          },
        },
      },

      dataToSendGare: {
        data: {
          gareDesignation: null,
          dateGiven: null,
          period: {
            dateDebut: null,
            dateFin: null,
          },
        },
      },

      offreVoyageStats: {
        data: {
          offreVoyageDesignation: null,
          dateGiven: null,
          period: {
            dateDebut: null,
            dateFin: null,
          },
        },
      },

      offreVoyageStatistique: null,
      selectedOfferDesignation: null,

      gareListObject: {
        data: {
          compagnieTransportRaisonSociale: null,
        },
      },
      gareDisponiblesList: [],
      gareDesignationSelected: null,

      photoProfilUrl: null,
      dataStatistics: null,
      dataStatisticGareTransport: null,
      dataStatisticsToday: null,
      datePrecise: null,
      dateDebut: null,
      dateFin: null,
    };
  },

  watch: {
    dateSelected(newVal) {
      if (!this.isInitialLoad && newVal) {
        this.applyFilterReservation();
      }
    },
    sectionGroup(newVal) {
      if (!this.isInitialLoad && newVal) {
        this.applyFilterReservation();
      }
    },
    dateDebut(newVal) {
      if (!this.isInitialLoad && newVal && this.dateSelected === 'Periodique') {
        this.applyFilterReservation();
      }
    },
    dateFin(newVal) {
      if (!this.isInitialLoad && newVal && this.dateSelected === 'Periodique') {
        this.applyFilterReservation();
      }
    },
    datePrecise(newVal) {
      if (!this.isInitialLoad && newVal && this.dateSelected === 'À une date précise') {
        this.applyFilterReservation();
      }
    },
    gareDesignationSelected(newVal) {
      if (!this.isInitialLoad && newVal && this.sectionGroup === 'Gare de transport') {
        this.applyFilterReservation();
      }
    },
  },

  methods: {
    // DELETE ALL DATAS GRAPH STATISTICS.

    deleteAllDataGraph() {
      this.chartDataGare.labels = [];
      this.chartDataGare.datasets[0].data = [];
      this.chartDataGare.datasets[1].data = [];

      this.chartDataDoughnutGare.labels = [];
      this.chartDataDoughnutGare.datasets[0].data = [];
    },

    // APPLIQUER LA RECHERCHE PAR FILTRE.
    async applyFilterReservation() {
      this.deleteAllDataGraph();
      this.isFilterApply = true;
      let periodDate = "";
      let section = "";

      // Déterminer la section
      switch (this.sectionGroup) {
        case "Ma compagnie":
          section = "compagnie";
          this.dataToSend.data.raisonSociale =
            this.$store.state.userAuthentified.compagnieTransportRaisonSociale;
          break;
        case "Gare de transport":
          section = "gare";
          this.dataToSendGare.data.gareDesignation =
            this.gareDesignationSelected;
          break;
        default:
          section = "compagnie";
          this.dataToSend.data.raisonSociale =
            this.$store.state.userAuthentified.compagnieTransportRaisonSociale;
          break;
      }

      // Déterminer la période
      switch (this.dateSelected) {
        case "Periodique":
          periodDate = "period";
          if (section === "compagnie" || section === "") {
            this.dataToSend.data.period.dateDebut = this.formatDate(this.dateDebut);
            this.dataToSend.data.period.dateFin = this.formatDate(this.dateFin);
          } else {
            this.dataToSendGare.data.period.dateDebut = this.formatDate(this.dateDebut);
            this.dataToSendGare.data.period.dateFin = this.formatDate(this.dateFin);
          }
          break;
        case "À une date précise":
          periodDate = "date";
          this.dataToSend.data.dateGiven = this.formatDate(this.datePrecise);
          this.dataToSendGare.data.dateGiven = this.formatDate(this.datePrecise);
          break;
        default:
          periodDate = "annuel";
          break;
      }

      try {
        const data = section === "compagnie" || section === "" 
          ? this.dataToSend.data 
          : this.dataToSendGare.data;
        
        const stats = await statisticsService.getReservationStatistics(
          periodDate,
          section,
          data
        );

        if (stats && stats.item) {
          if (section === "gare") {
            this.dataStatisticGareTransport = stats.item;
            this.getLabelAndDataSetGraphicsLineGare(this.dataStatisticGareTransport);
            this.getLabelAndDataSetGraphicsDoughnutGare(this.dataStatisticGareTransport);
          } else {
            // Pour la compagnie, on peut décommenter si nécessaire
            // this.dataStatistics = stats.item;
            // this.getLabelAndDataSetGraphicsLine(this.dataStatistics);
            // this.getLabelAndDataSetGraphicsDoughnut(this.dataStatistics);
          }
          this.$handleSuccess('Statistiques filtrées chargées');
        }
      } catch (error) {
        this.$handleError(error, 'Application des filtres');
      } finally {
        this.getStatisticsReservationOffreVoyageByYear();
        this.loading = false;
      }
    },
    // OBTENIR LES STATISTIQUES DES OFFRES DE VOYAGE
    async getStatisticsOffreVoyageCompagnie(offreVoyageDesignation) {
      let periodDate = "";
      this.offreVoyageStatistique = null;
      this.selectedOfferDesignation = offreVoyageDesignation;

      this.offreVoyageStats.data.offreVoyageDesignation = offreVoyageDesignation;
      
      // Déterminer la période
      switch (this.dateSelected) {
        case "Aujourd'hui":
          periodDate = "jour";
          break;
        case "Periodique":
          periodDate = "periode";
          this.offreVoyageStats.data.period.dateDebut = this.formatDate(this.dateDebut);
          this.offreVoyageStats.data.period.dateFin = this.formatDate(this.dateFin);
          break;
        case "À une date précise":
          periodDate = "date";
          this.offreVoyageStats.data.dateGiven = this.formatDate(this.datePrecise);
          break;
        case "Annuel":
          periodDate = "annuel";
          break;
        default:
          periodDate = "annuel";
          break;
      }

      try {
        const stats = await statisticsService.getOfferStatistics(
          periodDate,
          this.offreVoyageStats.data
        );

        if (stats && stats.item) {
          this.offreVoyageStatistique = stats.item;
        }
      } catch (error) {
        this.$handleError(error, 'Chargement des statistiques de l\'offre');
      } finally {
        this.loading = false;
      }
    },

    // OBTENIR LISTE DES OFFRES DE VOYAGES DISPONIBLES PAR COMPAGNIE
    async obtenirOffreVoyageListParCompagnie() {
      try {
        this.loading = true;
        const companyName = this.$store.state.userAuthentified.compagnieTransportRaisonSociale;
        const offers = await offerService.getOffersByCompany(companyName);
        this.offreVoyageDisponibleList = offers;
      } catch (error) {
        this.$handleError(error, 'Chargement des offres de voyage');
      } finally {
        this.loading = false;
      }
    },








    getLabelAndDataSetGraphicsLineGare(data) {
      if (this.dataStatisticGareTransport != null) {
        for (var [cle, valeur] of Object.entries(
          data.chiffreAffairesParProgramme
        )) {
          this.chartDataGare.labels.push(cle);
          this.chartDataGare.datasets[0].data.push(valeur);
        }

        if (
          data.chiffreAffairesBagagesParOffreVoyage != undefined ||
          data.chiffreAffairesBagagesParOffreVoyage != null
        ) {
          for (var value of Object.entries(
            data.chiffreAffairesBagagesParOffreVoyage
          )) {
            this.chartDataGare.datasets[1].data.push(value);
          }
        }
      }
    },

    getLabelAndDataSetGraphicsDoughnutGare(data) {
      if (this.dataStatisticGareTransport != null) {
        for (var [cle, valeur] of Object.entries(
          data.nombreReservationBilletVoyageParProgramme
        )) {
          this.chartDataDoughnutGare.labels.push(cle);
          this.chartDataDoughnutGare.datasets[0].data.push(valeur);
        }
      }
    },


    // Récupérer les statistiques de reservation des offre de voyage par an au chargement de la page
    async getStatisticsReservationOffreVoyageByYear() {
      try {
        this.dataToSend.data.raisonSociale =
          this.$store.state.userAuthentified.compagnieTransportRaisonSociale;
        
        const stats = await statisticsService.getYearStatistics(this.dataToSend.data);
        
        if (stats && stats.item) {
          this.dataStatistics = stats.item;
        }
      } catch (error) {
        this.$handleError(error, 'Chargement des statistiques annuelles');
      } finally {
        this.loading = false;
      }
    },

    // Récupérer les statistiques de reservation des offre de voyage à la date d'aujourd'hui au chargement de la page
    async getStatisticsReservationOffreVoyageToday() {
      try {
        this.dataToSend.data.raisonSociale =
          this.$store.state.userAuthentified.compagnieTransportRaisonSociale;
        
        const stats = await statisticsService.getTodayStatistics(this.dataToSend.data);
        
        if (stats && stats.item) {
          this.dataStatisticsToday = stats.item;
        }
      } catch (error) {
        this.$handleError(error, 'Chargement des statistiques du jour');
      } finally {
        this.loading = false;
      }
    },

    // Obtenir la photo de profil de l'utilisateur courant
    async getUrlPhotoProfil() {
      try {
        this.loading = true;
        const photoData = await documentService.getDocumentUrl({
          login: this.$store.state.userAuthentified.login,
          typeDocument: "PHOTO_PROFIL"
        });
        
        if (photoData && photoData.url) {
          this.photoProfilUrl = photoData.url;
          this.$store.commit("UPDATE_PICTURE_PROFIL", photoData.url);
        }
      } catch (error) {
        // Photo par défaut si erreur (pas d'alerte pour ne pas gêner l'utilisateur)
        this.photoProfilUrl = null;
      } finally {
        this.loading = false;
      }
    },

    clearOfferSelection() {
      this.offreVoyageStatistique = null;
      this.selectedOfferDesignation = null;
    },

    formatNumber,
    hasData,
    rankingHeight,

    // BARRES GROUPÉES : CA RÉSERVATIONS + CA BAGAGES, ALIGNÉS SUR LES MÊMES LIBELLÉS
    buildRevenueChart(stats, reservationKey, luggageKey) {
      return buildGroupedBars([
        { label: this.$t('dashboard.revenue'), map: stats && stats[reservationKey], color: SERIES_RESERVATION },
        { label: this.$t('dashboard.luggageRevenue'), map: stats && stats[luggageKey], color: SERIES_LUGGAGE },
      ]);
    },

    // CLASSEMENT HORIZONTAL DU NOMBRE DE RÉSERVATIONS
    buildRankingChart(stats, countKey) {
      return buildRanking(stats && stats[countKey], this.$t('dashboard.totalReservations'));
    },

    // FORMATER UN MONTANT
    formatCurrency(value) {
      if (value == null || value === 0) return '0 FCFA';
      return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(value) + ' FCFA';
    },

    // FORMAT DE LA DATE
    formatDate(date) {
      if (!date) return null;
      const [year, month, day] = date.split("-");
      return `${day}/${month}/${year}`;
    },

    // RECUPERER LA LISTE DES GARES ENREGISTRÉS PAR COMPAGNIE DE TRANSPORT
    async obtenirGareListParCompagnieTransport() {
      try {
        this.loading = true;
        const companyName = this.$store.state.userAuthentified.compagnieTransportRaisonSociale;
        const stations = await stationService.getStationsByCompany(companyName);
        this.gareDisponiblesList = stations;
      } catch (error) {
        this.$handleError(error, 'Chargement des gares');
      } finally {
        this.loading = false;
      }
    },
  },

  computed: {
    todayRevenueChart() {
      return this.buildRevenueChart(this.dataStatisticsToday, 'chiffreAffairesParOffreVoyage', 'chiffreAffairesBagagesParOffreVoyage');
    },
    todayRankingChart() {
      return this.buildRankingChart(this.dataStatisticsToday, 'nombreReservationBilletVoyageParOffreVoyage');
    },
    yearRevenueChart() {
      return this.buildRevenueChart(this.dataStatistics, 'chiffreAffairesParOffreVoyage', 'chiffreAffairesBagagesParOffreVoyage');
    },
    yearRankingChart() {
      return this.buildRankingChart(this.dataStatistics, 'nombreReservationBilletVoyageParOffreVoyage');
    },
    programRevenueChart() {
      return this.buildRevenueChart(this.offreVoyageStatistique, 'chiffreAffairesReservationParProgramme', 'chiffreAffairesBagageParProgramme');
    },
    programRankingChart() {
      return this.buildRankingChart(this.offreVoyageStatistique, 'nombreReservationBilletVoyageParProgramme');
    },

    // OPTIONS COMMUNES : AXES DISCRETS, GRILLE HORIZONTALE LÉGÈRE, INFOBULLE EN FCFA
    revenueChartOptions() {
      return chartOptions({ currency: true, legend: true });
    },
    rankingChartOptions() {
      return chartOptions({ currency: false, legend: false, horizontal: true });
    },

    chartStyles() {
      return CHART_STYLES;
    },

    activeOffersCount() {
      return this.offreVoyageDisponibleList.filter((o) => o.isActif).length;
    },

    currentDate() {
      return new Date().toLocaleDateString('fr-FR', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
      });
    },

    filteredOffreVoyageList() {
      if (this.filterActif === null) return this.offreVoyageDisponibleList;
      return this.offreVoyageDisponibleList.filter(o => o.isActif === this.filterActif);
    },

    getTextPeriod() {
      var text = "";
      switch (this.dateSelected) {
        case "Aujourd'hui":
          text = "Journalier";
          break;
        case "Periodique":
          text = "Période : " + this.dateDebut + " Au " + this.dateFin;
          break;
        case "À une date précise":
          text = "À la date du: " + this.datePrecise;
          break;

        case "Annuel":
          text = "Annuel";
          break;

        default:
          text = "Annuel";
          break;
      }
      return text;
    },
  },

  mounted() {
    this.getUrlPhotoProfil();
    this.obtenirGareListParCompagnieTransport();
    this.obtenirOffreVoyageListParCompagnie();
    this.getStatisticsReservationOffreVoyageByYear();
    this.getStatisticsReservationOffreVoyageToday();
    
    // Désactiver isInitialLoad après le chargement initial
    this.$nextTick(() => {
      setTimeout(() => {
        this.isInitialLoad = false;
      }, 1000);
    });
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/admin/ui";

/* EN-TÊTE */
.db-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 28px;
}

.db-head__date {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: $ov-green;
}

.db-head__title {
  margin: 4px 0 2px;
  font-size: 24px;
  font-weight: 900;
  letter-spacing: -0.4px;
  color: $ov-navy;
}

.db-head__sub {
  font-size: 13px;
  color: $ov-muted;
}

.db-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  .ov-select,
  .ov-input {
    width: auto;
    min-width: 170px;
    height: 40px;
    background-color: #ffffff;
  }
}

/* SECTIONS */
.db-section {
  margin-bottom: 36px;
}

.db-section__head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.db-section__title {
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  color: $ov-navy;
}

/* OFFRES */
.db-seg {
  display: inline-flex;
  padding: 3px;
  background: #f1f5f9;
  border-radius: 10px;
}

.db-seg__opt {
  height: 34px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  background: transparent;
  font-size: 13px;
  font-weight: 700;
  color: $ov-muted;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  &--active {
    background: #ffffff;
    color: $ov-navy;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12);
  }
}

.db-offer-name {
  font-weight: 700;
  color: $ov-navy;

  &--selected {
    color: $ov-green-dark;
  }
}

.db-actions {
  justify-content: flex-end;
}

.db-offer {
  margin-top: 20px;
  padding: 20px;
  border: 1px solid rgba(21, 158, 114, 0.3);
  border-radius: 16px;
  background: rgba(21, 158, 114, 0.03);
}

.db-offer__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.db-offer__title {
  font-size: 18px;
  font-weight: 800;
  color: $ov-navy;
}

@media (max-width: 760px) {
  .db-filters {
    width: 100%;

    .ov-select,
    .ov-input {
      flex: 1;
      min-width: 0;
    }
  }
}
</style>
